/**
 * Patient checkout AOE ("Ask on Order Entry") display + input helpers.
 *
 * The backend already returns normalized AOE questions (see
 * apps/labs/junction/aoe.py). This module adapts that shape for rendering and
 * input behavior in the lab checkout wizard, and selects the correct question
 * set for single vs combined panels.
 *
 * Answer submission rules (must match backend validate_aoe_answers):
 *   - text / numeric → submit the entered value
 *   - choice        → submit the selected answer CODE
 *   - multi_choice  → submit comma-separated answer CODES ("C1,C2")
 */

const SPECIMEN_SOURCE = {
  label: 'Specimen source',
  helperText: 'Enter the specimen source, such as Blood, Serum, or Urine.',
  placeholder: 'Blood',
};

function isSpecimenSource(question) {
  const code = String(question?.code || '').trim();
  const rawLabel = String(question?.raw_label || question?.rawLabel || '').trim().toLowerCase();
  return code === 'O006' || rawLabel === 'source';
}

function normalizeOptions(question) {
  const raw = Array.isArray(question?.options) ? question.options : [];
  return raw
    .map((opt) => {
      if (typeof opt === 'string') return { code: opt, label: opt };
      const code = String(opt?.code ?? opt?.value ?? '');
      const label = String(opt?.value ?? opt?.label ?? opt?.code ?? '');
      return { code, label: label || code };
    })
    .filter((opt) => opt.code !== '');
}

/**
 * Normalize one backend AOE question into a render-ready descriptor.
 */
export function normalizeAoeQuestion(question) {
  const type = String(question?.type || 'text').toLowerCase();
  const isChoice = type === 'choice';
  const isMultiChoice = type === 'multi_choice';
  const isNumeric = type === 'numeric';
  const isText = !isChoice && !isMultiChoice && !isNumeric;

  const backendLabel = question?.label || question?.raw_label || question?.rawLabel || '';
  const specimen = isSpecimenSource(question);

  return {
    markerId: String(question?.marker_id ?? question?.markerId ?? ''),
    questionId: String(question?.question_id ?? question?.questionId ?? ''),
    label: specimen ? SPECIMEN_SOURCE.label : (backendLabel || 'Lab question'),
    rawLabel: String(question?.raw_label ?? question?.rawLabel ?? backendLabel ?? ''),
    code: String(question?.code || ''),
    type,
    required: question?.required !== false,
    sequence: Number.isFinite(question?.sequence) ? question.sequence : 0,
    constraint: question?.constraint ?? null,
    defaultValue: question?.default ?? question?.defaultValue ?? null,
    biomarkerName: question?.biomarker_name || question?.biomarkerName || '',
    helperText: specimen ? SPECIMEN_SOURCE.helperText : '',
    placeholder: specimen ? SPECIMEN_SOURCE.placeholder : 'Enter answer',
    options: (isChoice || isMultiChoice) ? normalizeOptions(question) : [],
    isText,
    isChoice,
    isMultiChoice,
    isNumeric,
    isFastingDuplicate: Boolean(question?.is_fasting_duplicate ?? question?.isFastingDuplicate),
  };
}

function bySequence(a, b) {
  return (a.sequence || 0) - (b.sequence || 0);
}

/**
 * Pick the correct AOE question set for the active checkout.
 *
 * Combined panels expose AOE per method/assignment — we MUST use the selected
 * method's questions, never a shared "primary". Fasting duplicates are removed.
 */
export function getCheckoutAoeQuestions(lab, selectedMethod) {
  // A Program release can carry a combined method option before the tenant
  // runtime has set is_combined. Prefer the selected method whenever it
  // carries its own AOE; this also keeps standalone synthesized options
  // compatible with the same contract.
  const combinedMethodQuestions =
    Array.isArray(selectedMethod?.aoe_questions)
      ? selectedMethod.aoe_questions
      : null;

  const source = combinedMethodQuestions ?? (Array.isArray(lab?.aoe_questions) ? lab.aoe_questions : []);

  return source
    .filter((q) => !(q?.is_fasting_duplicate ?? q?.isFastingDuplicate))
    .map(normalizeAoeQuestion)
    .sort(bySequence);
}

/**
 * Return required questions that have no usable answer yet.
 * @param questions normalized questions (from getCheckoutAoeQuestions)
 * @param aoeAnswers map keyed by `${markerId}:${questionId}`
 */
export function getMissingRequiredAoe(questions, aoeAnswers) {
  const answers = aoeAnswers || {};
  return (questions || []).filter((q) => {
    if (!q.required) return false;
    const key = `${q.markerId}:${q.questionId}`;
    return !String(answers[key] || '').trim();
  });
}

export function aoeAnswerKey(question) {
  return `${question.markerId}:${question.questionId}`;
}
